import React, { Component } from 'react';
import { MapContainer, ZoomControl, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { WidgetHelper } from '../WidgetHelper';
import {
    IUsefullContact,
    IUsefullContactFilterParams,
    Dictionary,
    IPostalCode,
    ICoordinate,
} from '../../Models';
import L from 'leaflet';
import { UsefullContactService, PostalCodesService } from '../../Services';
import { cityDb } from '../../DexieDB';
import {
    DirectoryUsefullContactList,
    UsefullContactList,
} from '../../Components';

import TinySliderWrapper from '../../Components/TinySliderWrapper';

import { setWorkerUrl } from 'maplibre-gl';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';
import 'maplibre-gl/dist/maplibre-gl.css';

setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');

type BouncingMarker = {
    options: {
        id?: number;
    };
    dragging?: unknown;
    bounce?: () => void;
    stopBouncing?: () => void;
};

interface IDirectoryWidgetState {
    usefullContactList: IUsefullContact[];
    searchParams: IUsefullContactFilterParams;
    openMap: boolean;
    isMobile: boolean;
    zoom: number;
    height: string;
    fieldsOfSupportList: string[];
    typesList: string[];
    radiusKilometers: Dictionary<number, string>[];
    isLoading: boolean;
}

interface IDirectoryWidgetProps {
    languageFilter: string | null;
    eAppointmentUrl: string;
    homepageUrl: string;
    usefullContactDetailPage: string;
    usefullContactFocusedId: number;
    usefullContactPartnerName: string;
    dictClose: string;
    dictPostalCodeAndRadius: string;
    dictCancel: string;
    dictSave: string;
    dictType: string;
    dictField: string;
    dictFieldOfSupport: string;
    dictLanguage: string;
    dictTakeAppointment: string;
    dictPublicTransport: string;
    dictPostalCode: string;
    dictRadius: string;
    dictRemoveFilter: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictUsefullContactName: string;
    dictSearch: string;
    dictSearchUsefullContact: string;
    dictResult: string;
    dictResults: string;
    lang: string;
}

const OpenFreeMapLayer = () => {
    const map = useMap();

    React.useEffect(() => {
        const layer = maplibreGL({
            style: 'https://tiles.openfreemap.org/styles/positron',
        });

        layer.addTo(map);

        return () => {
            map.removeLayer(layer);
        };
    }, [map]);

    return null;
};

export class DirectoryWidget extends Component<
    IDirectoryWidgetProps,
    IDirectoryWidgetState
> {
    //private mapRef = createRef<LeafletMap>();
    private map?: L.Map;
    private markers: BouncingMarker[] = [];
    private cancellationToken: AbortController;
    readonly iconActiris: L.Icon;
    readonly iconAstro: L.Icon;
    readonly iconDefault: L.Icon;

    private mapWrapperRef = React.createRef<HTMLDivElement>();
    private ro?: ResizeObserver;

    constructor(props: any) {
        super(props);

        this.state = {
            usefullContactList: [],
            searchParams: {
                fieldsOfSupport: JSON.parse(
                    (
                        document.querySelector(
                            '#fieldOfSupportFilter'
                        ) as HTMLScriptElement
                    ).text
                ),
                type: JSON.parse(
                    (document.querySelector('#typeFilter') as HTMLScriptElement)
                        .text
                ),
                language: this.props.languageFilter
                    ? this.props.languageFilter
                    : null,
                postalCode: null,
                radius: null,
                title: null,
            },
            openMap: false,
            isMobile: window.innerWidth < 992,
            zoom: 12,
            height: '100%',
            fieldsOfSupportList: [],
            typesList: [],
            radiusKilometers: [],
            isLoading: true,
        };

        void cityDb.open();

        void this.getUsefullContact();
        void this.getAllUsefullContactTypeAndFieldOfSupport();

        this.iconActiris = new L.Icon({
            iconUrl: '/images/MapPin/pin_actiris.svg',
            iconSize: new L.Point(22, 27),
        });

        this.iconAstro = new L.Icon({
            iconUrl: '/images/MapPin/pin_astrotower.svg',
            iconSize: new L.Point(22, 27),
        });

        this.iconDefault = new L.Icon({
            iconUrl: '/images/MapPin/pin_partner.svg',
            iconSize: new L.Point(22, 27),
        });
    }

    componentDidMount() {
        window.addEventListener('resize', this.onResize);

        this.ro = new ResizeObserver(() => {
            this.map?.invalidateSize();
        });
        if (this.mapWrapperRef.current) {
            this.ro.observe(this.mapWrapperRef.current);
        }

        setTimeout(() => this.map?.invalidateSize(), 500);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
        this.ro?.disconnect();
    }

    componentDidUpdate() {
        if (this.map) {
            this.map.invalidateSize();
        }

        if (
            this.props.usefullContactFocusedId &&
            this.state.usefullContactList
        ) {
            this.setFocusedUsefullContact(this.props.usefullContactFocusedId);
        }
    }

    closeMap = () => {
        this.setState({ openMap: false, zoom: 12, height: '100%' });
    };

    InitializeDatabase = async () => {
        await cityDb.open();

        const count = await cityDb.city.count();
        if (count < 1) {
            const postalCodes = await PostalCodesService.getAllPostalCodes();
            await cityDb.city.bulkPut(postalCodes);
        }
    };

    getAllPostalCodes = async (inputValue?: string) => {
        await this.InitializeDatabase();
        const postalCodes: IPostalCode[] = [];
        if (inputValue) {
            const nameLabel =
                document.querySelector('html')!.lang == 'nl'
                    ? 'nameNL'
                    : 'nameFR';
            const cityList = await cityDb.city
                .where(nameLabel)
                .startsWithIgnoreCase(inputValue)
                .or('postalCodeString')
                .startsWithIgnoreCase(inputValue)
                .sortBy('postalCodeString');

            cityList.forEach(function (city) {
                const coordinate: ICoordinate = {
                    latitude: city.latitude,
                    longitude: city.longitude,
                };
                const cityLabel =
                    document.querySelector('html')!.lang == 'nl'
                        ? city.nameNL
                        : city.nameFR;
                postalCodes.push({
                    postalCode: +city.postalCode,
                    coordinate,
                    city: cityLabel,
                });
            });
        } else {
            const cityList = await cityDb.city
                .where('postalCode')
                .between(1000, 1999, true, true)
                .toArray();
            cityList.forEach(function (city) {
                const cityLabel =
                    document.querySelector('html')!.lang == 'nl'
                        ? city.nameNL
                        : city.nameFR;
                const coordinate: ICoordinate = {
                    latitude: city.latitude,
                    longitude: city.longitude,
                };
                postalCodes.push({
                    postalCode: +city.postalCode,
                    coordinate,
                    city: cityLabel,
                });
            });
        }
        return postalCodes;
    };

    getAllUsefullContactTypeAndFieldOfSupport = async () => {
        const typesList =
            await UsefullContactService.getAllUsefullContactType();
        const fieldsOfSupportList =
            await UsefullContactService.getAllFieldOfSupport();
        const radiusKilometers = this.getRadiusKilometers();

        this.setState({ fieldsOfSupportList, typesList, radiusKilometers });
    };

    getMarkerIcon = (usefullContact: IUsefullContact) => {
        if (usefullContact && usefullContact.types) {
            if (usefullContact.types.some((t) => t.technicalId === 1)) {
                return this.iconAstro;
            } else if (usefullContact.types.some((t) => t.technicalId === 2)) {
                return this.iconActiris;
            }
        }
        return this.iconDefault;
    };

    getRadiusKilometers = (): Dictionary<number, string>[] => {
        const radiusKilometers: Dictionary<number, string>[] = [];
        for (let i = 5; i <= 200; i += 5) {
            radiusKilometers.push({ label: i.toString(), value: i });
        }
        return radiusKilometers;
    };

    getUsefullContact = async (params = this.state.searchParams) => {
        this.markers = [];
        if (this.cancellationToken) {
            this.cancellationToken.abort();
        }

        this.cancellationToken = new AbortController();
        try {
            const allUsefullContactList =
                await UsefullContactService.getAllUsefullContact(
                    params,
                    this.cancellationToken.signal
                );
            this.setState({
                usefullContactList: allUsefullContactList,
                isLoading: false,
            });
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    };

    handleMapClick = () => {
        if (this.state.isMobile && !this.state.openMap) {
            this.setState({
                openMap: true,
                zoom: 11,
                height: 'calc(100vh - 50px)',
            });
        }
    };

    handleMarkerClick = (item: IUsefullContact) => {
        this.setFocusedUsefullContact(item.id);
    };

    onResize = () => {
        this.setState({ isMobile: window.innerWidth < 992 });
    };

    setFocusedUsefullContact = (usefullContactId: number) => {
        const focusToRemove = document.querySelector('.isFocus');
        if (focusToRemove) focusToRemove.classList.remove('isFocus');

        const focusToAdd = document.getElementById(usefullContactId.toString());
        if (focusToAdd) focusToAdd.classList.add('isFocus');

        const focused = document.querySelector<HTMLElement>('.isFocus');
        focused?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        const usefullContactList = this.state.usefullContactList;
        const usefullContactRemoveFocus = usefullContactList.find(
            (u) => u.isFocus === true
        );
        if (usefullContactRemoveFocus) {
            usefullContactRemoveFocus.isFocus = false;
        }
        const usefullContactToUpdate = usefullContactList.find((u) => u.id);
        if (usefullContactToUpdate) {
            usefullContactToUpdate.isFocus = true;
        }
    };

    startMarkerBounce = (usefullContactId: number) => {
        const marker = this.markers.find(
            (m) => m.options.id === usefullContactId
        );

        if (marker?.bounce && marker.dragging) {
            marker.bounce();
        }
    };

    stopMarkerBounce = (usefullContactId: number) => {
        const marker = this.markers.find(
            (m) => m.options.id === usefullContactId
        );

        marker?.stopBouncing?.();
    };

    updateUsefullContactFilterParams = (
        usefullContactFilterParams: IUsefullContactFilterParams
    ) => {
        this.setState(
            {
                searchParams: usefullContactFilterParams,
                isLoading: true,
            },
            () => {
                void this.getUsefullContact(usefullContactFilterParams);
            }
        );
    };

    render() {
        const openMap = this.state.openMap;
        const isMobile = this.state.isMobile;
        const settings = {
            items: 1,
            controls: false,
            nav: false,
            center: true,
        };

        return (
            <div className="map-container">
                <div
                    className={'map' + (this.state.openMap ? ' active' : '')}
                    id="map"
                    ref={this.mapWrapperRef}
                    onClick={this.handleMapClick}
                >
                    <MapContainer
                        ref={(map: L.Map | null) => {
                            if (map) {
                                this.map = map;
                                setTimeout(() => this.map?.invalidateSize(), 0);
                            }
                        }}
                        center={[50.850538, 4.253408]}
                        zoom={this.state.zoom}
                        scrollWheelZoom={false}
                        style={{
                            height: this.state.height,
                            width: '100%',
                            minHeight: 400,
                        }}
                        zoomControl={false}
                        maxZoom={19}
                    >
                        <OpenFreeMapLayer />
                        {!this.state.isLoading && (
                            <MarkerClusterGroup chunkedLoading={true}>
                                {this.state.usefullContactList.map((item) => {
                                    const latitude =
                                        typeof item.latitude === 'string'
                                            ? parseFloat(
                                                  item.latitude
                                                      .trim()
                                                      .replace(/,/g, '.')
                                              )
                                            : NaN;

                                    const longitude =
                                        typeof item.longitude === 'string'
                                            ? parseFloat(
                                                  item.longitude
                                                      .trim()
                                                      .replace(/,/g, '.')
                                              )
                                            : NaN;
                                    if (
                                        !Number.isFinite(latitude) ||
                                        !Number.isFinite(longitude)
                                    ) {
                                        return null;
                                    }

                                    const icon = this.getMarkerIcon(item);

                                    return (
                                        <Marker
                                            key={item.id}
                                            position={[latitude, longitude]}
                                            icon={icon}
                                            eventHandlers={{
                                                click: () =>
                                                    this.handleMarkerClick(
                                                        item
                                                    ),
                                            }}
                                            alt={item.title}
                                        />
                                    );
                                })}
                            </MarkerClusterGroup>
                        )}
                        {!isMobile && <ZoomControl position="bottomright" />}
                    </MapContainer>
                </div>
                <button className="close-map" onClick={this.closeMap}>
                    <i className="icon-cross"></i>
                    <span className="sr-only">{this.props.dictClose}</span>
                </button>

                {!openMap && (
                    <DirectoryUsefullContactList
                        usefullContactList={this.state.usefullContactList}
                        eAppointmentUrl={this.props.eAppointmentUrl}
                        filterUsefullContactList={
                            this.updateUsefullContactFilterParams
                        }
                        fieldsOfSupportList={this.state.fieldsOfSupportList}
                        postalCodesList={this.getAllPostalCodes}
                        typesList={this.state.typesList}
                        usefullContactPartnerName={
                            this.props.usefullContactPartnerName
                        }
                        radiusKilometers={this.state.radiusKilometers}
                        postalCodeAndRadius={this.props.dictPostalCodeAndRadius}
                        cancel={this.props.dictCancel}
                        save={this.props.dictSave}
                        type={this.props.dictType}
                        field={this.props.dictField}
                        fieldOfSupport={this.props.dictFieldOfSupport}
                        language={this.props.dictLanguage}
                        takeAppointment={this.props.dictTakeAppointment}
                        publicTransport={this.props.dictPublicTransport}
                        setBounce={this.startMarkerBounce}
                        stopBounce={this.stopMarkerBounce}
                        postalCode={this.props.dictPostalCode}
                        radius={this.props.dictRadius}
                        usefullContactFilterParams={this.state.searchParams}
                        dictRemoveFilter={this.props.dictRemoveFilter}
                        dictNoResultTitle={this.props.dictNoResultTitle}
                        dictNoResultDescription={
                            this.props.dictNoResultDescription
                        }
                        isLoading={this.state.isLoading}
                        dictUsefullContactName={
                            this.props.dictUsefullContactName
                        }
                        dictSearch={this.props.dictSearch}
                        dictSearchUsefullContact={
                            this.props.dictSearchUsefullContact
                        }
                        homepageUrl={this.props.homepageUrl}
                        usefullContactDetailPage={
                            this.props.usefullContactDetailPage
                        }
                        result={this.props.dictResult}
                        results={this.props.dictResults}
                        lang={this.props.lang}
                    />
                )}

                {isMobile && openMap && (
                    <div className="slider-contact map-tiles">
                        <TinySliderWrapper settings={settings}>
                            <UsefullContactList
                                usefullContactList={
                                    this.state.usefullContactList
                                }
                                eAppointmentUrl={this.props.eAppointmentUrl}
                                takeAppointment={this.props.dictTakeAppointment}
                                publicTransport={this.props.dictPublicTransport}
                                setBounce={this.startMarkerBounce}
                                stopBounce={this.stopMarkerBounce}
                                homepageUrl={this.props.homepageUrl}
                                usefullContactDetailPage={
                                    this.props.usefullContactDetailPage
                                }
                                lang={this.props.lang}
                            />
                        </TinySliderWrapper>
                    </div>
                )}
            </div>
        );
    }
}

WidgetHelper.register(DirectoryWidget, 'directory-widget');
